import React, { useState, useEffect, useCallback } from "react";
import OfferStepper from "../offers/status/stepper/OfferStepper";
import { getOffer } from "../../../../../services/staffingService";

/**
 * SentOffersTab — extracted from the legacy offer.jsx Tab 2.
 * - Horizontal officer pill nav (color-themed by status)
 * - Inlined OfferStepper for the selected offer (kills the separate /offer/:offerId route)
 */
const getStatusTheme = (status, selected) => {
  const themes = {
    pending:  { selected: "bg-yellow-50 text-yellow-800 border-yellow-400",  unselected: "bg-white border-yellow-200 text-yellow-700 hover:border-yellow-300" },
    accepted: { selected: "bg-green-50 text-green-800 border-green-400",     unselected: "bg-white border-green-200 text-green-700 hover:border-green-300" },
    rejected: { selected: "bg-red-50 text-red-800 border-red-400",           unselected: "bg-white border-red-200 text-red-700 hover:border-red-300" },
    disputed: { selected: "bg-orange-50 text-orange-800 border-orange-400",  unselected: "bg-white border-orange-200 text-orange-700 hover:border-orange-300" },
    expired:  { selected: "bg-gray-50 text-gray-800 border-gray-400",        unselected: "bg-white border-gray-200 text-gray-700 hover:border-gray-300" },
  };
  const theme = themes[status?.toLowerCase()] || {
    selected: "bg-blue-50 text-blue-800 border-blue-400",
    unselected: "bg-white border-blue-200 text-blue-700 hover:border-blue-300",
  };
  return selected ? theme.selected : theme.unselected;
};

const statusDotColor = (status) =>
  ({
    pending:  "bg-yellow-500",
    accepted: "bg-green-500",
    rejected: "bg-red-500",
    disputed: "bg-orange-500",
    expired:  "bg-gray-500",
  }[status?.toLowerCase()] || "bg-blue-500");

const SentOffersTab = ({ providerCompanyId, jobId, offersMetadata = [] }) => {
  const [selectedOfferId, setSelectedOfferId] = useState(
    offersMetadata.length > 0 ? offersMetadata[0]._id : null
  );
  const [offer, setOffer] = useState({});
  const [negotiations, setNegotiations] = useState({});
  const [loading, setLoading] = useState(false);

  // Keep selectedOfferId in sync when offersMetadata first arrives
  useEffect(() => {
    if (!selectedOfferId && offersMetadata.length > 0) {
      setSelectedOfferId(offersMetadata[0]._id);
    }
  }, [offersMetadata, selectedOfferId]);

  const fetchOfferData = useCallback(async () => {
    if (!selectedOfferId) return;
    setLoading(true);
    try {
      const { data } = await getOffer(providerCompanyId, selectedOfferId);
      setOffer(data.offer);
      setNegotiations(data.offer.negotiations);
    } catch (err) {
      console.error("Error fetching offer:", err);
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId, selectedOfferId]);

  useEffect(() => {
    fetchOfferData();
  }, [fetchOfferData]);

  if (offersMetadata.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-12 text-center text-sm text-muted-foreground">
        No offers sent yet. Switch to the Recommendations tab to send one.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Officer pills nav */}
      <div className="mb-4 flex-shrink-0">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {offersMetadata.map((o) => {
            const user = o.provider_resource_id?.user_id;
            const profileName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Unknown";
            const isSelected = o._id === selectedOfferId;

            return (
              <button
                key={o._id}
                onClick={() => setSelectedOfferId(o._id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 whitespace-nowrap ${getStatusTheme(o.status, isSelected)} ${isSelected ? "shadow-md border-2" : "hover:shadow-sm border"}`}
              >
                <div className={`w-6 h-6 rounded-full overflow-hidden flex-shrink-0 ${isSelected ? "ring-1 ring-white ring-opacity-60" : ""}`}>
                  {user?.profilePictureUrl ? (
                    <img src={user.profilePictureUrl} alt={profileName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                      {profileName.split(" ").map((n) => n[0]).join("")}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium leading-none">{profileName}</span>
                  <span className="text-xs opacity-75 leading-none mt-0.5">
                    {o.status?.charAt(0).toUpperCase() + o.status?.slice(1)}
                    {o.status?.toLowerCase() === "disputed" && " - Action Needed"}
                  </span>
                </div>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDotColor(o.status)}`} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Inlined stepper for the selected offer */}
      {selectedOfferId && Object.keys(offer).length > 0 && (
        <div className="flex-1 min-h-0 bg-white rounded-lg border border-border flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <OfferStepper
              offer={offer}
              negotiations={negotiations}
              setNegotiations={setNegotiations}
              setOffer={setOffer}
              offerId={selectedOfferId}
              providerCompanyId={providerCompanyId}
              jobId={jobId}
            />
          </div>
        </div>
      )}

      {selectedOfferId && Object.keys(offer).length === 0 && !loading && (
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground text-sm">Loading offer details…</div>
        </div>
      )}
    </div>
  );
};

export default SentOffersTab;
